import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Store, Search, Eye, X, ToggleLeft, ToggleRight, Mail, Phone, MapPin, Key, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminVendors() {
  const { token } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeOwner, setStoreOwner] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStores(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const toggleStoreStatus = async (storeId, currentStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stores/${storeId}/status?is_active=${!currentStatus}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(currentStatus ? "Mağaza kapatıldı" : "Mağaza açıldı");
        fetchStores();
      }
    } catch (err) {
      console.error(err);
      toast.error("İşlem başarısız");
    }
  };

  const viewStoreDetail = async (store) => {
    setSelectedStore(store);
    // Mağaza sahibini getir
    try {
      const res = await fetch(`${API_URL}/api/admin/users/by-store/${store.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const owner = await res.json();
        setStoreOwner(owner);
      }
    } catch (err) {
      console.error(err);
    }
    setShowModal(true);
  };

  const openPasswordReset = () => {
    // Rastgele şifre oluştur
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let pass = "";
    for (let i = 0; i < 8; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(pass);
    setShowPasswordModal(true);
  };

  const resetPassword = async () => {
    if (!storeOwner || !newPassword) return;
    
    setResettingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${storeOwner.id}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });
      
      if (res.ok) {
        toast.success("Şifre başarıyla sıfırlandı!");
      } else {
        toast.error("Şifre sıfırlanamadı");
      }
    } catch (err) {
      console.error(err);
      toast.error("Bir hata oluştu");
    }
    setResettingPassword(false);
  };

  const copyCredentials = () => {
    const text = `E-posta: ${storeOwner?.email}\nŞifre: ${newPassword}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Kopyalandı!");
    setTimeout(() => setCopied(false), 2000);
  };

  const filteredStores = stores.filter(store => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      store.name?.toLowerCase().includes(searchLower) ||
      store.owner_email?.toLowerCase().includes(searchLower) ||
      store.phone?.includes(search)
    );
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mağaza Yönetimi</h1>
          <p className="text-gray-500">Toplam {stores.length} mağaza</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-green-600">Aktif Mağazalar</p>
          <p className="text-2xl font-bold text-green-700">{stores.filter(s => s.is_active).length}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-sm text-red-600">Pasif Mağazalar</p>
          <p className="text-2xl font-bold text-red-700">{stores.filter(s => !s.is_active).length}</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-600">Toplam Mağaza</p>
          <p className="text-2xl font-bold text-blue-700">{stores.length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Mağaza adı, e-posta veya telefon ile ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      {/* Stores Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mağaza</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İletişim</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Şehir</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ürün</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                  </div>
                </td>
              </tr>
            ) : filteredStores.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                  Mağaza bulunamadı
                </td>
              </tr>
            ) : (
              filteredStores.map((store) => (
                <tr key={store.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Store className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{store.name}</p>
                        <p className="text-sm text-gray-500">{store.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="flex items-center gap-1 text-gray-600">
                        <Mail className="w-4 h-4" />
                        {store.owner_email || "-"}
                      </p>
                      <p className="flex items-center gap-1 text-gray-600">
                        <Phone className="w-4 h-4" />
                        {store.phone || "-"}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      {store.city || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="font-bold text-gray-900">{store.product_count || 0}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      store.is_active 
                        ? "bg-green-100 text-green-700" 
                        : "bg-red-100 text-red-700"
                    }`}>
                      {store.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => viewStoreDetail(store)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Detay & Şifre Sıfırla"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => toggleStoreStatus(store.id, store.is_active)}
                        className={`p-2 rounded-lg ${
                          store.is_active 
                            ? "text-red-600 hover:bg-red-50" 
                            : "text-green-600 hover:bg-green-50"
                        }`}
                        title={store.is_active ? "Pasif Yap" : "Aktif Yap"}
                      >
                        {store.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Store Detail Modal */}
      {showModal && selectedStore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">{selectedStore.name}</h2>
              <button onClick={() => { setShowModal(false); setStoreOwner(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Kullanıcı Bilgileri - ÖNEMLİ */}
              {storeOwner && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Giriş Bilgileri
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-blue-600">Kullanıcı Adı</p>
                      <p className="font-medium">{storeOwner.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600">E-posta (Giriş için)</p>
                      <p className="font-bold text-blue-900">{storeOwner.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={openPasswordReset}
                    className="mt-4 w-full py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 flex items-center justify-center gap-2"
                  >
                    <Key className="w-5 h-5" />
                    Şifre Sıfırla
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Mağaza ID</p>
                  <p className="font-mono text-sm">{selectedStore.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Durum</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedStore.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {selectedStore.is_active ? "Aktif" : "Pasif"}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">Açıklama</p>
                <p>{selectedStore.description || "Açıklama yok"}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Telefon</p>
                  <p>{selectedStore.phone || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Şehir</p>
                  <p>{selectedStore.city || "-"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Vergi No</p>
                  <p>{selectedStore.tax_number || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Vergi Dairesi</p>
                  <p>{selectedStore.tax_office || "-"}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">IBAN</p>
                <p className="font-mono">{selectedStore.iban || "-"}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Adres</p>
                <p>{selectedStore.address || "-"}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Kayıt Tarihi</p>
                <p>{selectedStore.created_at ? new Date(selectedStore.created_at).toLocaleDateString("tr-TR") : "-"}</p>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  toggleStoreStatus(selectedStore.id, selectedStore.is_active);
                  setShowModal(false);
                }}
                className={`px-4 py-2 rounded-lg font-medium ${
                  selectedStore.is_active 
                    ? "bg-red-100 text-red-700 hover:bg-red-200" 
                    : "bg-green-100 text-green-700 hover:bg-green-200"
                }`}
              >
                {selectedStore.is_active ? "Mağazayı Kapat" : "Mağazayı Aç"}
              </button>
              <button
                onClick={() => { setShowModal(false); setStoreOwner(null); }}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && storeOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Şifre Sıfırla</h2>
              <p className="text-gray-500 text-sm">{storeOwner.name} için yeni şifre</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">E-posta</p>
                <p className="font-bold">{storeOwner.email}</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Yeni Şifre</label>
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-lg font-mono text-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Otomatik oluşturuldu. İsterseniz değiştirebilirsiniz.</p>
              </div>

              <button
                onClick={resetPassword}
                disabled={resettingPassword}
                className="w-full py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50"
              >
                {resettingPassword ? "Sıfırlanıyor..." : "Şifreyi Sıfırla"}
              </button>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 mb-2">Şifre sıfırlandıktan sonra satıcıya gönderin:</p>
                <div className="bg-white rounded p-3 font-mono text-sm">
                  <p>E-posta: {storeOwner.email}</p>
                  <p>Şifre: {newPassword}</p>
                </div>
                <button
                  onClick={copyCredentials}
                  className="mt-2 w-full py-2 bg-green-600 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-green-700"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Kopyalandı!" : "Bilgileri Kopyala"}
                </button>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
